import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, Shield, Lock, Layers, Copy, FilePen, RefreshCw,
  Plus, ChevronDown, CheckCircle2, Circle, Clock, Eye, Stamp,
  Edit3, Trash2, X, AlertTriangle, Link2, Calendar, StickyNote,
  Package, FileCheck, PenTool, FolderPlus, Asterisk, ArrowRight,
  Info, Sparkles
} from "lucide-react";
import PremiumModal from "../ui/PremiumModal";
import { WILL_TYPES } from "./willTypeTemplates";
import type { WillTypeConfig, DocumentPackageTemplate } from "./willTypeTemplates";

// ── Types ─────────────────────────────────────────────────────────────────────
interface DocumentItem {
  key: string;
  label: string;
  required: boolean;
  category: "primary" | "supporting" | "signing" | "custom";
  status: "not_started" | "drafting" | "client_review" | "final" | "executed";
  assignedDrafterId: string | null;
  dueDate: string | null;
  draftDocumentId: string | null;
  finalDocumentId: string | null;
  currentVersion: number;
  signingRequirements: {
    signers: { role: string; name: string | null; signed: boolean; signedAt: Date | null }[];
    witnessed: boolean;
    notarized: boolean;
    witnessCount: number;
  };
  mirrorGroup: string | null;
  notes: string | null;
}

interface Props {
  matterId: string;
  caseData: any;
  onUpdate: () => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const ICON_MAP: Record<string, React.FC<any>> = {
  FileText, Shield, Lock, Layers, Copy, FilePen, RefreshCw,
};

const STATUS_CONFIGS: Record<string, { label: string; color: string; bgColor: string; dotColor: string }> = {
  not_started:   { label: "Not Started",    color: "text-slate-600",   bgColor: "bg-slate-100",   dotColor: "bg-slate-400" },
  drafting:      { label: "Drafting",       color: "text-blue-700",    bgColor: "bg-blue-100",    dotColor: "bg-blue-500" },
  client_review: { label: "Client Review",  color: "text-amber-700",   bgColor: "bg-amber-100",   dotColor: "bg-amber-500" },
  final:         { label: "Final",          color: "text-purple-700",  bgColor: "bg-purple-100",  dotColor: "bg-purple-500" },
  executed:      { label: "Executed",       color: "text-emerald-700", bgColor: "bg-emerald-100", dotColor: "bg-emerald-500" },
};

const CATEGORY_CONFIGS: Record<string, { label: string; Icon: React.FC<any>; color: string; bgColor: string }> = {
  primary:    { label: "Primary Documents",    Icon: FileCheck, color: "text-blue-600",    bgColor: "bg-blue-50" },
  supporting: { label: "Supporting Documents", Icon: FolderPlus, color: "text-amber-600",  bgColor: "bg-amber-50" },
  signing:    { label: "Signing Materials",    Icon: PenTool,   color: "text-purple-600",  bgColor: "bg-purple-50" },
  custom:     { label: "Custom Documents",     Icon: StickyNote, color: "text-slate-600",  bgColor: "bg-slate-100" },
};

const CATEGORY_ORDER: ("primary" | "supporting" | "signing" | "custom")[] = ["primary", "supporting", "signing", "custom"];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "not_started", label: "Not Started" },
  { value: "drafting", label: "Drafting" },
  { value: "client_review", label: "Client Review" },
  { value: "final", label: "Final" },
  { value: "executed", label: "Executed" },
];

const emptyCustomDoc: Omit<DocumentItem, "status" | "currentVersion" | "assignedDrafterId" | "draftDocumentId" | "finalDocumentId"> = {
  key: "", label: "", required: false, category: "custom",
  dueDate: null, mirrorGroup: null, notes: null,
  signingRequirements: { signers: [], witnessed: false, notarized: false, witnessCount: 0 },
};

// ── Component ─────────────────────────────────────────────────────────────────
const EstateDocumentPackageTab: React.FC<Props> = ({ matterId, caseData, onUpdate }) => {
  const documentPackage: DocumentItem[] = caseData?.documentPackage || [];
  const willType: string | null = caseData?.willType || null;

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ primary: true, supporting: true, signing: true, custom: true });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedWillType, setSelectedWillType] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);

  // Add custom doc form
  const [addForm, setAddForm] = useState<{ label: string; category: string; required: boolean; dueDate: string; notes: string }>({ label: "", category: "custom", required: false, dueDate: "", notes: "" });

  // Edit form
  const [editForm, setEditForm] = useState<{ status: string; dueDate: string; notes: string }>({ status: "not_started", dueDate: "", notes: "" });

  // ── Derived data ────────────────────────────────────────────────────────────
  const activeWillConfig = useMemo(() => WILL_TYPES.find(w => w.value === willType) || null, [willType]);

  const grouped = useMemo(() => {
    const g: Record<string, { doc: DocumentItem; idx: number }[]> = { primary: [], supporting: [], signing: [], custom: [] };
    documentPackage.forEach((d, i) => {
      const cat = d.category || "custom";
      if (g[cat]) g[cat].push({ doc: d, idx: i });
      else g.custom.push({ doc: d, idx: i });
    });
    return g;
  }, [documentPackage]);

  const statusCounts = useMemo(() => {
    const c = { not_started: 0, drafting: 0, client_review: 0, final: 0, executed: 0 };
    documentPackage.forEach(d => {
      if (c[d.status as keyof typeof c] !== undefined) c[d.status as keyof typeof c]++;
    });
    return c;
  }, [documentPackage]);

  const totalDocs = documentPackage.length;
  const executedCount = statusCounts.executed;

  // ── Handlers ────────────────────────────────────────────────────────────────
  const toggleSection = (cat: string) => setExpandedSections(prev => ({ ...prev, [cat]: !prev[cat] }));

  const handleApplyTemplate = async (typeValue: string) => {
    setApplying(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const config = WILL_TYPES.find(w => w.value === typeValue);
      if (!config) return;

      // Set the willType on the case
      await estatesApi.update(caseData._id, { willType: typeValue });

      // Add each document from template
      for (const tmpl of config.documents) {
        const item: Omit<DocumentItem, "_id"> = {
          key: tmpl.key,
          label: tmpl.label,
          required: tmpl.required,
          category: tmpl.category,
          status: "not_started",
          assignedDrafterId: null,
          dueDate: null,
          draftDocumentId: null,
          finalDocumentId: null,
          currentVersion: 1,
          signingRequirements: tmpl.signingRequirements,
          mirrorGroup: tmpl.mirrorGroup || null,
          notes: null,
        };
        await estatesApi.addSubdoc(caseData._id, "documentPackage", item);
      }
      onUpdate();
      setShowTypeSelector(false);
      setSelectedWillType(null);
    } catch (err) {
      console.error("Failed to apply template:", err);
    } finally {
      setApplying(false);
    }
  };

  const handleAddCustomDoc = async () => {
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      const item = {
        key: `custom_${Date.now()}`,
        label: addForm.label,
        required: addForm.required,
        category: addForm.category,
        status: "not_started",
        assignedDrafterId: null,
        dueDate: addForm.dueDate || null,
        draftDocumentId: null,
        finalDocumentId: null,
        currentVersion: 1,
        signingRequirements: { signers: [], witnessed: false, notarized: false, witnessCount: 0 },
        mirrorGroup: null,
        notes: addForm.notes || null,
      };
      await estatesApi.addSubdoc(caseData._id, "documentPackage", item);
      onUpdate();
      setShowAddModal(false);
      setAddForm({ label: "", category: "custom", required: false, dueDate: "", notes: "" });
    } catch (err) {
      console.error("Failed to add document:", err);
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (doc: DocumentItem, idx: number) => {
    setEditForm({
      status: doc.status,
      dueDate: doc.dueDate ? new Date(doc.dueDate).toISOString().split("T")[0] : "",
      notes: doc.notes || "",
    });
    setEditingIdx(idx);
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (editingIdx === null) return;
    setSaving(true);
    try {
      const { estatesApi } = await import("../../services/api");
      await estatesApi.updateSubdoc(caseData._id, "documentPackage", editingIdx, {
        status: editForm.status,
        dueDate: editForm.dueDate || null,
        notes: editForm.notes || null,
      });
      onUpdate();
      setShowEditModal(false);
      setEditingIdx(null);
    } catch (err) {
      console.error("Failed to update document:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (idx: number, newStatus: string) => {
    try {
      const { estatesApi } = await import("../../services/api");
      await estatesApi.updateSubdoc(caseData._id, "documentPackage", idx, { status: newStatus });
      onUpdate();
    } catch (err) {
      console.error("Failed to update status:", err);
    }
  };

  const handleDelete = async (idx: number) => {
    try {
      const { estatesApi } = await import("../../services/api");
      await estatesApi.removeSubdoc(caseData._id, "documentPackage", idx);
      onUpdate();
      setShowDeleteConfirm(null);
    } catch (err) {
      console.error("Failed to remove document:", err);
    }
  };

  const formatLabel = (s: string) => s?.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || "";

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* ── Will Type Header / Selector ───────────────────────────────────────── */}
      {!willType && documentPackage.length === 0 ? (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="text-center mb-8">
            <div className="h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Select Will Type</h2>
            <p className="text-sm text-slate-500 max-w-lg mx-auto">
              Choose the type of estate plan to automatically populate the document package with the appropriate templates and signing requirements.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WILL_TYPES.map(wt => {
              const IconComp = ICON_MAP[wt.icon] || FileText;
              const isSelected = selectedWillType === wt.value;
              return (
                <motion.button
                  key={wt.value}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedWillType(isSelected ? null : wt.value)}
                  className={`relative text-left p-5 rounded-xl border-2 transition-all ${
                    isSelected
                      ? "border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/20 shadow-sm"
                      : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`h-10 w-10 rounded-xl bg-${wt.color}-50 flex items-center justify-center flex-shrink-0`}>
                      <IconComp className={`h-5 w-5 text-${wt.color}-600`} />
                    </div>
                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      </div>
                    )}
                  </div>
                  <h3 className={`font-semibold mb-1 ${isSelected ? "text-emerald-800" : "text-slate-800"}`}>{wt.label}</h3>
                  <p className="text-xs text-slate-500 leading-relaxed mb-3">{wt.description}</p>
                  <div className="flex items-center gap-1.5 text-xs text-slate-400">
                    <FileText className="h-3 w-3" />
                    <span>{wt.documents.length} documents</span>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {selectedWillType && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 flex justify-center">
              <button
                onClick={() => handleApplyTemplate(selectedWillType)}
                disabled={applying}
                className="flex items-center gap-2 px-6 py-3 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {applying ? (
                  <>
                    <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                    Applying Template...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Apply Template
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
            </motion.div>
          )}
        </motion.div>
      ) : (
        <>
          {/* ── Header with will type badge ─────────────────────────────────── */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <Package className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-slate-800">Document Package</h2>
                    {activeWillConfig && (() => {
                      const WtIcon = ICON_MAP[activeWillConfig.icon] || FileText;
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-${activeWillConfig.color}-100 text-${activeWillConfig.color}-700`}>
                          <WtIcon className="h-3 w-3" />
                          {activeWillConfig.label}
                        </span>
                      );
                    })()}
                  </div>
                  <p className="text-sm text-slate-500">
                    {totalDocs} {totalDocs === 1 ? "document" : "documents"} in package
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {willType && (
                  <button
                    onClick={() => setShowTypeSelector(true)}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                  >
                    Change Type
                  </button>
                )}
                <button
                  onClick={() => {
                    setAddForm({ label: "", category: "custom", required: false, dueDate: "", notes: "" });
                    setShowAddModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors"
                >
                  <Plus className="h-4 w-4" /> Add Document
                </button>
              </div>
            </div>

            {/* ── Progress Summary Bar ───────────────────────────────────────── */}
            {totalDocs > 0 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-emerald-700">{executedCount}</span> of{" "}
                    <span className="font-semibold">{totalDocs}</span> documents executed
                  </p>
                  <p className="text-xs text-slate-400">
                    {totalDocs > 0 ? Math.round((executedCount / totalDocs) * 100) : 0}% complete
                  </p>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden flex">
                  {(["executed", "final", "client_review", "drafting", "not_started"] as const).map(status => {
                    const count = statusCounts[status];
                    if (count === 0) return null;
                    const pct = (count / totalDocs) * 100;
                    return (
                      <motion.div
                        key={status}
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.5, ease: "easeOut" }}
                        className={`h-full ${STATUS_CONFIGS[status].dotColor}`}
                        title={`${STATUS_CONFIGS[status].label}: ${count}`}
                      />
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  {Object.entries(STATUS_CONFIGS).map(([key, cfg]) => (
                    <div key={key} className="flex items-center gap-1.5 text-xs text-slate-500">
                      <div className={`h-2 w-2 rounded-full ${cfg.dotColor}`} />
                      {cfg.label} ({statusCounts[key as keyof typeof statusCounts]})
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Empty state ─────────────────────────────────────────────────── */}
          {totalDocs === 0 && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <div className="h-16 w-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-emerald-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No Documents in Package</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
                Add documents to the package manually, or change the will type to apply a template.
              </p>
              <button
                onClick={() => {
                  setAddForm({ label: "", category: "custom", required: false, dueDate: "", notes: "" });
                  setShowAddModal(true);
                }}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] transition-colors"
              >
                <Plus className="h-4 w-4" /> Add First Document
              </button>
            </motion.div>
          )}

          {/* ── Document Categories ────────────────────────────────────────── */}
          <AnimatePresence>
            {CATEGORY_ORDER.map(cat => {
              const items = grouped[cat];
              if (items.length === 0) return null;
              const catCfg = CATEGORY_CONFIGS[cat];
              const CatIcon = catCfg.Icon;
              const isOpen = expandedSections[cat];

              return (
                <motion.div
                  key={cat}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(cat)}
                    className="w-full flex items-center justify-between p-4 hover:bg-slate-50/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-9 w-9 rounded-xl ${catCfg.bgColor} flex items-center justify-center`}>
                        <CatIcon className={`h-4.5 w-4.5 ${catCfg.color}`} />
                      </div>
                      <h3 className="font-semibold text-slate-800">{catCfg.label}</h3>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600`}>
                        {items.length}
                      </span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 space-y-2">
                          {items.map(({ doc, idx }, i) => {
                            const statusCfg = STATUS_CONFIGS[doc.status] || STATUS_CONFIGS.not_started;
                            return (
                              <motion.div
                                key={doc.key + idx}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: i * 0.03 }}
                                className="group flex items-start gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-sm transition-all bg-slate-50/30"
                              >
                                {/* Status dot */}
                                <div className={`h-3 w-3 rounded-full ${statusCfg.dotColor} mt-1.5 flex-shrink-0`} />

                                {/* Main content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-slate-800 text-sm">{doc.label}</span>
                                    {doc.required && (
                                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600">
                                        <Asterisk className="h-2.5 w-2.5" /> Required
                                      </span>
                                    )}
                                    {doc.mirrorGroup && (
                                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-pink-50 text-pink-600">
                                        <Link2 className="h-2.5 w-2.5" /> Paired
                                      </span>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                    {/* Status dropdown */}
                                    <select
                                      value={doc.status}
                                      onChange={(e) => handleStatusChange(idx, e.target.value)}
                                      onClick={(e) => e.stopPropagation()}
                                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-500/30 ${statusCfg.bgColor} ${statusCfg.color}`}
                                    >
                                      {STATUS_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>

                                    {doc.dueDate && (
                                      <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                                        <Calendar className="h-3 w-3" />
                                        {new Date(doc.dueDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                                      </span>
                                    )}

                                    <span className="text-xs text-slate-400">v{doc.currentVersion}</span>

                                    {/* Signing summary */}
                                    {doc.signingRequirements && (
                                      <div className="flex items-center gap-1.5">
                                        {doc.signingRequirements.witnessed && (
                                          <span className="inline-flex items-center gap-0.5 text-[10px] text-blue-500">
                                            <Eye className="h-2.5 w-2.5" /> {doc.signingRequirements.witnessCount}W
                                          </span>
                                        )}
                                        {doc.signingRequirements.notarized && (
                                          <span className="inline-flex items-center gap-0.5 text-[10px] text-purple-500">
                                            <Stamp className="h-2.5 w-2.5" /> Notary
                                          </span>
                                        )}
                                        {doc.signingRequirements.signers?.length > 0 && (
                                          <span className="text-[10px] text-slate-400">
                                            {doc.signingRequirements.signers.length} signer{doc.signingRequirements.signers.length > 1 ? "s" : ""}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  {doc.notes && (
                                    <p className="text-xs text-slate-400 mt-1 italic truncate">{doc.notes}</p>
                                  )}
                                </div>

                                {/* Action buttons (hover) */}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openEditModal(doc, idx); }}
                                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
                                  >
                                    <Edit3 className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(idx); }}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </>
      )}

      {/* ── Add Custom Document Modal ────────────────────────────────────────── */}
      <PremiumModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Custom Document"
        subtitle="Add a custom document to the package"
        icon={Plus}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Document Label *</label>
            <input
              value={addForm.label}
              onChange={e => setAddForm({ ...addForm, label: e.target.value })}
              placeholder="e.g., Gift Letter, Separation Agreement"
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            <div className="grid grid-cols-4 gap-2">
              {CATEGORY_ORDER.map(cat => {
                const cfg = CATEGORY_CONFIGS[cat];
                const CIcon = cfg.Icon;
                const isSelected = addForm.category === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setAddForm({ ...addForm, category: cat })}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-center ${
                      isSelected ? "border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/20" : "border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <CIcon className={`h-4 w-4 ${isSelected ? "text-emerald-600" : cfg.color}`} />
                    <span className={`text-[11px] font-medium ${isSelected ? "text-emerald-700" : "text-slate-600"}`}>
                      {cfg.label.split(" ")[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={addForm.required}
                onChange={e => setAddForm({ ...addForm, required: e.target.checked })}
                className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              Required document
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
            <input
              type="date"
              value={addForm.dueDate}
              onChange={e => setAddForm({ ...addForm, dueDate: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={addForm.notes}
              onChange={e => setAddForm({ ...addForm, notes: e.target.value })}
              rows={2}
              placeholder="Additional notes about this document..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              onClick={() => setShowAddModal(false)}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddCustomDoc}
              disabled={!addForm.label.trim() || saving}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Adding..." : "Add Document"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Edit Document Modal ──────────────────────────────────────────────── */}
      <PremiumModal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingIdx(null); }}
        title="Edit Document"
        subtitle={editingIdx !== null ? documentPackage[editingIdx]?.label : ""}
        icon={Edit3}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Status</label>
            <div className="flex gap-2 flex-wrap">
              {STATUS_OPTIONS.map(opt => {
                const cfg = STATUS_CONFIGS[opt.value];
                const isSelected = editForm.status === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setEditForm({ ...editForm, status: opt.value })}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                      isSelected ? cfg.bgColor + " " + cfg.color : "bg-slate-50 text-slate-400 hover:bg-slate-100"
                    }`}
                  >
                    <div className={`h-2 w-2 rounded-full ${isSelected ? cfg.dotColor : "bg-slate-300"}`} />
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
            <input
              type="date"
              value={editForm.dueDate}
              onChange={e => setEditForm({ ...editForm, dueDate: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={editForm.notes}
              onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
              rows={3}
              placeholder="Notes about this document..."
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {/* Signing requirements (read-only summary) */}
          {editingIdx !== null && documentPackage[editingIdx]?.signingRequirements && (
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
              <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                <Info className="h-3 w-3" /> Signing Requirements
              </h4>
              <div className="space-y-2">
                {documentPackage[editingIdx].signingRequirements.signers?.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <PenTool className="h-3.5 w-3.5 text-slate-400" />
                    <span>
                      {documentPackage[editingIdx].signingRequirements.signers.length} signer{documentPackage[editingIdx].signingRequirements.signers.length > 1 ? "s" : ""}:
                      {" "}{documentPackage[editingIdx].signingRequirements.signers.map((s: any) => formatLabel(s.role)).join(", ")}
                    </span>
                  </div>
                )}
                {documentPackage[editingIdx].signingRequirements.witnessed && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Eye className="h-3.5 w-3.5 text-blue-400" />
                    <span>{documentPackage[editingIdx].signingRequirements.witnessCount} witness{documentPackage[editingIdx].signingRequirements.witnessCount !== 1 ? "es" : ""} required</span>
                  </div>
                )}
                {documentPackage[editingIdx].signingRequirements.notarized && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Stamp className="h-3.5 w-3.5 text-purple-400" />
                    <span>Notarization required</span>
                  </div>
                )}
                {!documentPackage[editingIdx].signingRequirements.witnessed &&
                 !documentPackage[editingIdx].signingRequirements.notarized &&
                 documentPackage[editingIdx].signingRequirements.signers?.length === 0 && (
                  <p className="text-sm text-slate-400 italic">No formal signing requirements</p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              onClick={() => { setShowEditModal(false); setEditingIdx(null); }}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleEditSave}
              disabled={saving}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Delete Confirmation Modal ────────────────────────────────────────── */}
      <PremiumModal
        open={showDeleteConfirm !== null}
        onClose={() => setShowDeleteConfirm(null)}
        title="Remove Document"
        size="sm"
        icon={AlertTriangle}
      >
        <div className="p-4 text-center">
          <p className="text-sm text-slate-600 mb-4">
            Remove <strong>{showDeleteConfirm !== null ? documentPackage[showDeleteConfirm]?.label : ""}</strong> from the document package?
            This action cannot be undone.
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
            <button onClick={() => showDeleteConfirm !== null && handleDelete(showDeleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700">Remove</button>
          </div>
        </div>
      </PremiumModal>

      {/* ── Change Will Type Modal ────────────────────────────────────────────── */}
      <PremiumModal
        open={showTypeSelector}
        onClose={() => { setShowTypeSelector(false); setSelectedWillType(null); }}
        title="Change Will Type"
        subtitle="Changing the type will not remove existing documents"
        icon={Package}
        size="xl"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <span>Applying a new template will add its documents to the existing package. Duplicates will not be created for items with matching keys.</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {WILL_TYPES.map(wt => {
              const IconComp = ICON_MAP[wt.icon] || FileText;
              const isCurrent = willType === wt.value;
              const isSelected = selectedWillType === wt.value;
              return (
                <button
                  key={wt.value}
                  onClick={() => setSelectedWillType(isSelected ? null : wt.value)}
                  disabled={isCurrent}
                  className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                    isCurrent
                      ? "border-slate-300 bg-slate-50 opacity-60 cursor-not-allowed"
                      : isSelected
                        ? "border-emerald-500 bg-emerald-50/50 ring-2 ring-emerald-500/20"
                        : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <IconComp className={`h-4 w-4 text-${wt.color}-600`} />
                    <span className="font-medium text-sm text-slate-800">{wt.label}</span>
                    {isCurrent && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-500">Current</span>}
                    {isSelected && <CheckCircle2 className="h-4 w-4 text-emerald-500 ml-auto" />}
                  </div>
                  <p className="text-xs text-slate-500">{wt.description}</p>
                </button>
              );
            })}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <button onClick={() => { setShowTypeSelector(false); setSelectedWillType(null); }} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
            <button
              onClick={() => selectedWillType && handleApplyTemplate(selectedWillType)}
              disabled={!selectedWillType || applying}
              className="px-5 py-2.5 bg-[#0F5132] text-white rounded-xl text-sm font-medium hover:bg-[#146C43] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {applying ? "Applying..." : "Apply Template"}
            </button>
          </div>
        </div>
      </PremiumModal>
    </div>
  );
};

export default EstateDocumentPackageTab;
